import React from 'react';
import { ExternalChip } from '../../lib/types';
import { formatDate } from '../../lib/utils';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import DetailSection from '../../components/DetailSection/DetailSection';
import Button from '../../components/Button/Button';

interface ExternalChipDetailModalProps {
  open: boolean;
  onClose: () => void;
  chip: ExternalChip | null;
  onClaim: (chip: ExternalChip) => void;
}

export default function ExternalChipDetailModal({ open, onClose, chip, onClaim }: ExternalChipDetailModalProps) {
  if (!chip) return null;

  // Open the next modal in the same React commit as we close this one — both
  // state updates batch inside the click handler, so the swap happens in one
  // paint frame. The previous setTimeout-after-onClose pattern produced a
  // visible 200ms gap where neither modal was rendered.
  function handleClaim() {
    onClaim(chip!);
    onClose();
  }

  const fullAddress = chip.ownerStreet
    ? [
        chip.ownerStreet,
        chip.ownerApt ? `Apt ${chip.ownerApt}` : '',
        `${chip.ownerCity}, ${chip.ownerState} ${chip.ownerZip}`,
      ]
        .filter(Boolean)
        .join(', ')
    : null;

  return (
    <Modal open={open} onClose={onClose} size="extra-wide">
      <ModalTitle>Chip details</ModalTitle>

      <DetailSection
        title="Pet Information"
        fields={[
          { label: 'Pet Name', value: chip.petName },
          { label: 'Species', value: chip.species },
          { label: 'Breed', value: chip.breed ? `${chip.breed}${chip.mixedBreed ? ' (mixed)' : ''}` : null },
          { label: 'Gender', value: chip.gender },
          { label: 'Date of Birth', value: formatDate(chip.dob) },
          { label: 'Spayed/Neutered', value: chip.spayedNeutered },
          { label: 'Color/Markings', value: chip.color },
          { label: 'Weight', value: chip.weight ? `${chip.weight} lbs` : null },
        ]}
      />

      {chip.assignedTo === 'adopter' && chip.ownerName && (
        <DetailSection
          title="Owner"
          fields={[
            { label: 'Name', value: chip.ownerName },
            { label: 'Email', value: chip.ownerEmail },
            { label: 'Phone', value: chip.ownerPhone },
            { label: 'Address', value: fullAddress },
          ]}
        />
      )}

      {chip.assignedTo === 'rescue' && (
        <DetailSection title="Owner" fields={[{ label: 'Status', value: 'No owner (rescue-held)' }]} />
      )}

      <DetailSection
        title="Organization"
        fields={[
          { label: 'Organization', value: chip.organization },
          { label: 'Chip ID', value: chip.id },
          { label: 'Full ID', value: chip.fullChipId },
          { label: 'Registered', value: formatDate(chip.registeredDate) },
          { label: 'Last Updated', value: formatDate(chip.lastUpdated) },
        ]}
      />

      <ModalActions>
        <Button variant="green" onClick={handleClaim}>
          Add to My Organization
        </Button>
      </ModalActions>
    </Modal>
  );
}
